<script setup>
import { computed, inject, ref } from 'vue';
import { _mft } from '../../../../i18n.js';

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        require: true
    },
    content: {
        type: Object,
        required: true
    }
});

const formUrl = computed(() => {
    return route('block-editor-container.blocks.lead-capture.store', {
        containerRef: props.containerRef,
        blockUuid: props.uuid
    })
});
const text = computed(() => props.content.text);
const description = computed(() => props.content.description);
const policyUrl = computed(() => props.content.policy);
const redirectOnSuccess = computed(() => !!props.content.redirect);
const successMessage = computed(() => props.content.success_message ?? "Your details have been saved");
const submitLabel = computed(() => props.content.submit_label?.trim() || _mft('pagebuilderView:leadCapture.submit'));

const collectTitle = computed(() => props.content.collect_title ?? false);
const collectPhone = computed(() => props.content.collect_phone ?? false);
const collectLinkedin = computed(() => props.content.collect_linkedin ?? false);
const collectCompany = computed(() => props.content.collect_company ?? true);
const collectOrgNr = computed(() => props.content.collect_org_nr ?? false);
const collectWebsite = computed(() => props.content.collect_website ?? false);
const notificationsEnabled = computed(() => props.content.notifications_enabled ?? false);
const captchaEnabled = computed(() => props.content.captcha_enabled ?? true);
const turnstileFrameBase = document.querySelector('meta[name="turnstile-frame-url"]')?.content ?? '';
const captchaIsActive = computed(() => captchaEnabled.value && turnstileFrameBase !== '');
const captchaSolved = ref(false);
const captchaFrame = ref(null);
const submitError = ref('');

const onCaptchaStatus = (status) => {
    captchaSolved.value = status === 'solved';
};

const firstErrorMessage = (error) => {
    const errors = error?.response?.data?.errors;
    const first = errors ? Object.values(errors)[0] : null;

    if (Array.isArray(first)) {
        return first[0] ?? '';
    }

    return typeof first === 'string' ? first : '';
};

const onSubmit = () => {
    submitError.value = '';
};

const onFailure = (error) => {
    // The form validates every field in the browser, so a rejection here is
    // almost always the captcha. Say so instead of failing silently.
    submitError.value = firstErrorMessage(error) || _mft('pagebuilderView:leadCapture.submitFailed');

    // Turnstile accepts each token once, so the one we just sent is spent —
    // reload the widget rather than leaving the visitor resubmitting a dead token.
    captchaFrame.value?.retry();
};

const $document = inject('$document');
const onSuccess = () => {
    if (redirectOnSuccess.value) {
        $document.locationAssign(props.content.redirect);
    }
};
</script>
<template>
    <div class="my-6">
        <h2 class="my-6 text-2xl text-center md:text-4xl lg:text-5xl" v-html="text"></h2>
        <div class="my-6 text-center" v-html="description"></div>

        <axios-form
            :action="formUrl"
            method="POST"
            @submit="onSubmit"
            @success="onSuccess"
            @failure="onFailure"
        >
            <input v-if="props.content" type="hidden" name="leadTag" :value="props.content.leadTag" />
            <input v-if="notificationsEnabled" type="hidden" name="notifications_enabled" value="1" />

            <label class="block mb-1" for="`${uuid}-name`">{{ _mft('pagebuilderView:leadCapture.labels.name') }}*</label>
            <input type="text" maxlength="255" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-name`" name="name" required>

            <label class="block mb-1" for="`${uuid}-email`">{{ _mft('pagebuilderView:leadCapture.labels.email') }}*</label>
            <input type="email" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-email`" name="email" required>

            <template v-if="collectTitle">
                <label class="block mb-1" for="`${uuid}-title`">{{ _mft('pagebuilderView:leadCapture.labels.title') }}*</label>
                <input type="text" maxlength="255" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-title`" name="title" required>
            </template>

            <template v-if="collectPhone">
                <label class="block mb-1" for="`${uuid}-phone`">{{ _mft('pagebuilderView:leadCapture.labels.phone') }}*</label>
                <input type="tel" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-phone`" name="phone" required>
            </template>

            <template v-if="collectLinkedin">
                <label class="block mb-1" for="`${uuid}-linkedin`">{{ _mft('pagebuilderView:leadCapture.labels.linkedIn') }}*</label>
                <input type="text" maxlength="255" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-linkedin`" name="linkedin" required>
            </template>

            <template v-if="collectCompany">
                <label class="block mb-1" for="`${uuid}-company`">{{ _mft('pagebuilderView:leadCapture.labels.company') }}*</label>
                <input type="text" maxlength="255" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-company`" name="company" required>
            </template>

            <template v-if="collectOrgNr">
                <label class="block mb-1" for="`${uuid}-org_nr`">{{ _mft('pagebuilderView:leadCapture.labels.corporateIdentityNumber') }}*</label>
                <input type="text" maxlength="255" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-org_nr`" name="org_nr" required>
            </template>

            <template v-if="collectWebsite">
                <label class="block mb-1" for="`${uuid}-website`">{{ _mft('pagebuilderView:leadCapture.labels.website') }}*</label>
                <input type="text" maxlength="255" class="myflow-basic-input rounded-custom shadow-custom" id="`${uuid}-website`" name="website" required>
            </template>

            <label class="block mb-3">
                <input type="checkbox" class="mr-1 text-black" required> {{ _mft('pagebuilderView:leadCapture.acceptPolicy.text') }} <a :href="policyUrl" target="_blank" class="text-inherit"><u>{{ _mft('pagebuilderView:leadCapture.acceptPolicy.link') }}</u></a>
            </label>

            <div class="md:flex" :class="captchaIsActive ? 'md:justify-between items-start' : 'md:justify-end'">
                <turnstile-frame
                    v-if="captchaIsActive"
                    ref="captchaFrame"
                    :frame-base="turnstileFrameBase"
                    action="lead-capture"
                    @update:status="onCaptchaStatus"
                />

                <div class="flex justify-end" :class="captchaIsActive ? 'mt-12' : ''">
                    <captcha-submit-button
                        v-if="captchaIsActive"
                        class="inline-block px-4 py-2 mt-2 overflow-hidden rounded-custom shadow-custom"
                        :solved="captchaSolved"
                    >
                        {{ submitLabel }}
                    </captcha-submit-button>
                    <button-primary
                        v-else
                        type="submit"
                        class="inline-block px-4 py-2 mt-2 overflow-hidden rounded-custom shadow-custom"
                    >
                        {{ submitLabel }}
                    </button-primary>
                </div>
            </div>

            <p v-if="submitError" class="mt-2 text-sm text-center">{{ submitError }}</p>

            <template v-if="!redirectOnSuccess" #success-content>
                <div class="flex items-center justify-center w-full mt-4 md:mt-6 lg:mt-12">
                    <p class="flex items-center justify-center p-5 shadow-custom rounded-custom tint-background">
                        {{ successMessage }}
                    </p>
                </div>
            </template>

        </axios-form>
    </div>
</template>
